import { RefObject, useEffect, useState } from 'react';

/**
 * Returns a state variable which corresponds to the amount of unused space in the container element,
 * determined by finding the last visible item (from left right) and subtracting its X position and
 * width from the total width of the container.
 * @param containerRef ref of the container
 * @returns number of pixels extra space available
 */
export function useRemainingSpace(containerRef: RefObject<HTMLElement>): number {
  const [remainingSpace, setRemainingSpace] = useState<number>(0);

  useEffect(() => {
    const updateRemainingSpace = () => {
      if (!containerRef?.current) return;
      const width = containerRef.current.getBoundingClientRect().width ?? 0;

      const lastVisibleItem = Array.from(containerRef.current.children)
        .reverse()
        .find((item) => {
          if (
            item instanceof HTMLElement &&
            item.checkVisibility({ visibilityProperty: true }) &&
            item.dataset?.['targetid']
          ) {
            return true;
          }
          return false;
        });
      const lastVisibleItemWidth = lastVisibleItem?.getBoundingClientRect().width ?? 0;
      const lastVisibleItemRect = lastVisibleItem?.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      const lastVisibleItemX = lastVisibleItemRect
        ? lastVisibleItemRect.left - containerRect.left
        : 0;
      // remaining space is width - the last visible item's X pos - its width
      setRemainingSpace(width - lastVisibleItemX - lastVisibleItemWidth);
    };

    updateRemainingSpace();
    window.addEventListener('resize', updateRemainingSpace);

    return () => {
      window.removeEventListener('resize', updateRemainingSpace);
    };
  });

  return remainingSpace;
}
